<template>
  <v-app style="background-color: transparent !important; min-height: auto !important;">
    <section v-if="showBpjsRegistration" class="patient-coverage-card mb-6">
      <div class="patient-coverage-heading">
        <div>
          <div class="patient-coverage-eyebrow">Jenis pendaftaran</div>
          <h5 class="mb-1">Pasien menggunakan BPJS?</h5>
          <p class="mb-0">Pilih BPJS untuk mengambil identitas peserta secara otomatis.</p>
        </div>
        <div class="patient-coverage-options" role="radiogroup" aria-label="Jenis pendaftaran pasien">
          <label :class="['patient-coverage-option', { active: form.coverage_type === 'umum' }]">
            <input v-model="form.coverage_type" type="radio" value="umum" @change="coverageChanged" />
            <span class="coverage-radio"></span>
            <i class="fas fa-wallet"></i>
            <span><strong>Umum</strong><small>Tanpa BPJS</small></span>
          </label>
          <label :class="['patient-coverage-option', { active: form.coverage_type === 'bpjs' }]">
            <input v-model="form.coverage_type" type="radio" value="bpjs" @change="coverageChanged" />
            <span class="coverage-radio"></span>
            <i class="fas fa-id-card"></i>
            <span><strong>BPJS/JKN</strong><small>Verifikasi peserta</small></span>
          </label>
        </div>
      </div>

      <div v-if="form.coverage_type === 'bpjs'" class="bpjs-lookup-panel">
        <div class="row align-items-end">
          <div class="col-lg-4 col-md-5 mb-3 mb-md-0">
            <label class="font-weight-bold text-dark-75 mb-2">Cari peserta menggunakan</label>
            <div class="bpjs-lookup-type" role="group" aria-label="Pilih identitas peserta">
              <button
                type="button"
                :class="{ active: bpjsLookupType === 'nokartu' }"
                @click="setBpjsLookupType('nokartu')"
              >
                Nomor Kartu
              </button>
              <button
                type="button"
                :class="{ active: bpjsLookupType === 'nik' }"
                @click="setBpjsLookupType('nik')"
              >
                NIK
              </button>
            </div>
          </div>
          <div class="col-lg-5 col-md-4 mb-3 mb-md-0">
            <label class="font-weight-bold text-dark-75 mb-2">
              {{ bpjsLookupType === 'nik' ? 'NIK peserta' : 'Nomor Kartu BPJS' }}
            </label>
            <div class="bpjs-input-shell">
              <i class="fas fa-id-card"></i>
              <input
                v-model.trim="bpjsLookupNumber"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                :placeholder="bpjsLookupType === 'nik' ? 'Masukkan NIK peserta' : 'Masukkan nomor kartu tanpa spasi'"
                @input="onBpjsNumberInput"
                @keyup.enter.prevent="lookupBpjsParticipant"
              />
            </div>
          </div>
          <div class="col-lg-3 col-md-3">
            <button
              type="button"
              class="btn bpjs-lookup-button w-100"
              :disabled="bpjsLoading"
              @click="lookupBpjsParticipant"
            >
              <b-spinner v-if="bpjsLoading" small class="mr-2" />
              <i v-else class="fas fa-search mr-2"></i>
              {{ bpjsLoading ? 'Memeriksa...' : 'Cek Peserta' }}
            </button>
          </div>
        </div>

        <div v-if="bpjsError" class="bpjs-message is-error mt-3">
          <i class="fas fa-exclamation-circle"></i><span>{{ bpjsError }}</span>
        </div>
        <div v-if="bpjsVerified" class="bpjs-message is-success compact mt-3">
          <i class="fas fa-check-circle"></i>
          <div>
            <strong>{{ bpjsParticipant.nama }} terverifikasi aktif</strong>
            <span>Data peserta yang tersedia sudah diterapkan ke form.</span>
          </div>
        </div>
      </div>
    </section>
    <div class="row">
      <!-- Avatar Section -->
      <div class="col-lg-3 col-md-4 mb-6">
        <div class="card card-custom border-0 shadow-sm rounded-xl patient-photo-card">
          <div class="card-body p-5">
            <div class="patient-photo-card__heading">
              <div>
                <span>FOTO PASIEN</span>
                <h5>Foto Profil</h5>
              </div>
              <small>Opsional</small>
            </div>

            <div
              v-if="uploadMethod === 'upload'"
              class="patient-photo-dropzone"
              :class="{
                'is-dragging': photoDragging,
                'has-photo': hasProfilePhoto,
              }"
              role="button"
              tabindex="0"
              :aria-label="hasProfilePhoto ? 'Ganti foto pasien' : 'Pilih foto pasien'"
              @click="openPhotoPicker"
              @keydown.enter.prevent="openPhotoPicker"
              @keydown.space.prevent="openPhotoPicker"
              @dragenter.prevent="photoDragging = true"
              @dragover.prevent="photoDragging = true"
              @dragleave.prevent="leavePhotoDropzone"
              @drop.prevent="dropPhoto"
            >
              <input
                ref="photoInput"
                type="file"
                accept="image/jpeg,image/png"
                aria-hidden="true"
                @click.stop
                @change="onPhotoInput"
              />

              <template v-if="hasProfilePhoto">
                <img
                  :src="profilePhotoPreview"
                  alt="Preview foto pasien"
                  @load="onImageLoad()"
                />
                <span class="patient-photo-dropzone__overlay">
                  <i class="fas fa-camera"></i> Klik atau tarik foto untuk mengganti
                </span>
              </template>
              <div v-else class="patient-photo-dropzone__empty">
                <span><i class="fas fa-cloud-upload-alt"></i></span>
                <strong>Tarik & lepas foto di sini</strong>
                <small>atau klik untuk memilih dari perangkat</small>
                <em>JPG atau PNG · maksimal 4 MB</em>
              </div>
            </div>

            <div v-else class="patient-camera-stage">
              <vue-web-cam
                v-if="!captureStatus"
                ref="webcam"
                :device-id="deviceId"
                width="100%"
                height="100%"
                @started="onStarted"
                @stop="onStopped"
                @error="onError"
                @cameras="onCameras"
                @camera-change="onCameraChange"
              />
              <img v-else :src="baseImg" alt="Preview foto dari kamera" />
            </div>

            <div class="patient-photo-meta">
              <template v-if="selectedPhotoFile">
                <span><i class="fas fa-check-circle"></i> Siap disimpan</span>
                <strong>{{ selectedPhotoFile.name }}</strong>
                <small>{{ formatFileSize(selectedPhotoFile.size) }}</small>
              </template>
              <template v-else-if="captureStatus">
                <span><i class="fas fa-check-circle"></i> Hasil kamera siap disimpan</span>
              </template>
              <template v-else-if="hasProfilePhoto">
                <span><i class="fas fa-shield-alt"></i> Foto pasien saat ini</span>
                <small>Klik area foto untuk mengganti</small>
              </template>
              <template v-else>
                <span class="is-muted">Belum ada foto dipilih</span>
              </template>
            </div>

            <div class="patient-photo-actions">
              <button
                v-if="uploadMethod === 'upload'"
                type="button"
                class="patient-photo-actions__primary"
                data-icon-contrast="light"
                @click="openPhotoPicker"
              >
                <i class="fas fa-folder-open"></i>
                {{ hasProfilePhoto ? "Ganti foto" : "Pilih foto" }}
              </button>
              <button
                v-else-if="!captureStatus"
                type="button"
                class="patient-photo-actions__primary"
                data-icon-contrast="light"
                @click="onCapture"
              >
                <i class="fas fa-camera"></i> Ambil foto
              </button>
              <button
                v-else
                type="button"
                class="patient-photo-actions__primary"
                data-icon-contrast="light"
                @click="retakePhoto"
              >
                <i class="fas fa-redo"></i> Ambil ulang
              </button>

              <button
                type="button"
                class="patient-photo-actions__secondary"
                @click="changeUploadBtn"
              >
                <i :class="uploadMethod === 'upload' ? 'fas fa-camera' : 'fas fa-upload'"></i>
                {{ uploadMethod === "upload" ? "Kamera" : "Unggah" }}
              </button>

              <button
                v-if="hasProfilePhoto || selectedPhotoFile || captureStatus"
                type="button"
                class="patient-photo-actions__danger"
                aria-label="Hapus foto pasien"
                title="Hapus foto"
                @click="resetImg"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
            <small v-if="error.photo" class="patient-photo-error">{{ error.photo }}</small>
          </div>
        </div>
      </div>

      <div class="col-md">
        <b-form
          @submit.stop.prevent="formOnSubmit"
          enctype="multipart/form-data"
        >
          <div class="card card-custom border-0 shadow-sm rounded-xl mb-6">
            <div class="card-header bg-transparent border-0 pt-6 pb-0">
              <h5 class="card-title font-weight-bolder text-dark-75 mb-0">Data Pribadi</h5>
            </div>
            <div class="card-body p-6 patient-form-grid patient-form-grid--personal">
              
              <!-- Input Title -->
              <div class="form-group mb-4 patient-form-grid__wide">
                <label class="font-weight-bold text-dark-75 mb-3 d-block">Gelar:</label>
                <div class="d-flex flex-wrap gap-2">
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="Tn" name="customRadio" class="custom-control-input" value="Tn" v-model="form.title" @change="setGender" />
                    <label class="custom-control-label" for="Tn">Tuan (Tn)</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="Ny" name="customRadio" class="custom-control-input" value="Ny" v-model="form.title" @change="setGender" />
                    <label class="custom-control-label" for="Ny">Nyonya (Ny)</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="Nn" name="customRadio" class="custom-control-input" value="Nn" v-model="form.title" @change="setGender" />
                    <label class="custom-control-label" for="Nn">Nona (Nn)</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="An" name="customRadio" class="custom-control-input" value="An" v-model="form.title" />
                    <label class="custom-control-label" for="An">Anak (An)</label>
                  </div>
                </div>
                <small class="d-block text-danger mt-1">{{ error.title }}</small>
              </div>

              <!-- Input patient id -->
              <div class="form-group mb-4">
                <label for="input-patien-id" class="font-weight-bold text-dark-75 mb-2">
                  ID Pasien: <em class="text-muted font-weight-normal" v-if="edit == false">opsional</em>
                </label>
                <b-form-input
                  id="input-patien-id"
                  v-model="form.patient_id"
                  placeholder="Id Pasien"
                  @keyup="checkPatientId(form.patient_id)"
                  class="form-control-solid rounded-lg"
                ></b-form-input>
                <small class="text-muted mt-1 d-block" v-if="edit == false">*Jika Kosong, Sistem Akan Menggenerate Otomatis ID</small>
                <small class="text-danger">{{ error.patient_id }}</small>
              </div>

              <!-- Input Name -->
              <div class="form-group mb-4">
                <label for="input-name" class="font-weight-bold text-dark-75 mb-2">Nama Lengkap:</label>
                <b-form-input
                  id="input-name"
                  v-model="form.name"
                  placeholder="Nama Lengkap"
                  class="form-control-solid rounded-lg"
                  @input="clearBpjsAutofill('name')"
                ></b-form-input>
                <transition name="bpjs-note">
                  <small v-if="hasBpjsAutofill('name')" class="bpjs-autofill-note">
                    <i class="fas fa-check-circle"></i> Diisi otomatis dari data BPJS
                    <button type="button" aria-label="Tutup penanda" @click="clearBpjsAutofill('name')">&times;</button>
                  </small>
                </transition>
                <small class="text-danger">{{ error.name }}</small>
              </div>

          <!-- Input Ktp Number -->
          <b-form-group id="input-group-ktp">
            <label for="input-ktp" class="d-block">Nomor KTP:</label>
            <!-- <b-form-input
              id="input-ktp"
              v-model="form.id_card_number"
              placeholder="Nomor KTP"
            >
            </b-form-input> -->
            <b-input-group class="mt-3">
              <b-form-input
                id="input-ktp"
                placeholder="Nomor KTP"
                v-model="form.id_card_number"
                @keyup="resetSsPatientId"
                @input="clearBpjsAutofill('id_card_number')"
              ></b-form-input>
              <b-input-group-append
                v-if="
                  ss_organization_id != null &&
                  ss_organization_id != 'null' &&
                  ss_organization_id != 'undefined' &&
                  ss_organization_id != ''
                "
              >
                <b-button variant="primary" @click="satuSehatConnect()"
                  >Cek NIK Satu Sehat</b-button
                >
              </b-input-group-append>
            </b-input-group>
            <transition name="bpjs-note">
              <small v-if="hasBpjsAutofill('id_card_number')" class="bpjs-autofill-note">
                <i class="fas fa-check-circle"></i> NIK diisi otomatis dari data BPJS
                <button type="button" aria-label="Tutup penanda" @click="clearBpjsAutofill('id_card_number')">&times;</button>
              </small>
            </transition>
            <small class="text-danger">{{ error.id_card_number }}</small>
          </b-form-group>

              <!-- Input Date of Birth -->
              <div class="form-group mb-4 patient-form-grid__wide">
                <label class="font-weight-bold text-dark-75 mb-2">Tempat & Tanggal Lahir:</label>
                <div class="row">
                  <div class="col-md-6 mb-2 mb-md-0">
                    <b-form-input
                      id="input-birth-place"
                      placeholder="Tempat Lahir (opsional)"
                      v-model="form.birt_place"
                      class="form-control-solid rounded-lg"
                    ></b-form-input>
                    <small class="text-danger mt-1 d-block">{{ error.birt_place }}</small>
                  </div>
                  <div class="col-md-6">
                    <v-dialog ref="dialog" v-model="modal2" :return-value.sync="form.date" persistent width="350px">
                      <template v-slot:activator="{ on, attrs }">
                        <div class="input-group input-group-solid">
                          <div class="input-group-prepend">
                            <span class="input-group-text bg-light border-0 cursor-pointer" v-bind="attrs" v-on="on">
                              <i class="fas fa-calendar text-primary"></i>
                            </span>
                          </div>
                          <b-form-input
                            id="input-trigger-modal-patient"
                            v-model="form.birt_date"
                            placeholder="Tanggal Lahir (YYYY-MM-DD)"
                            @input="onBirthDateInput"
                            class="form-control-solid border-0"
                            style="border-radius: 0 0.5rem 0.5rem 0;"
                          ></b-form-input>
                        </div>
                      </template>
                      <v-date-picker v-if="modal2" v-model="form.birt_date" locale="id">
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="modal2 = false">Cancel</v-btn>
                        <v-btn text color="primary" @click="$refs.dialog.save(form.birt_date)">OK</v-btn>
                      </v-date-picker>
                    </v-dialog>
                    <transition name="bpjs-note">
                      <small v-if="hasBpjsAutofill('birt_date')" class="bpjs-autofill-note">
                        <i class="fas fa-check-circle"></i> Tanggal lahir diisi otomatis dari data BPJS
                        <button type="button" aria-label="Tutup penanda" @click="clearBpjsAutofill('birt_date')">&times;</button>
                      </small>
                    </transition>
                    <small class="text-danger mt-1 d-block">{{ error.birt_date }}</small>
                  </div>
                </div>
              </div>

              <div class="form-group mb-4">
                <label class="font-weight-bold text-dark-75 mb-3 d-block">Status: <em class="text-muted font-weight-normal">opsional</em></label>
                <div class="d-flex flex-wrap gap-2">
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="merried" name="status" class="custom-control-input" value="Menikah" v-model="form.status" />
                    <label class="custom-control-label" for="merried">Menikah</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="notMerried" name="status" class="custom-control-input" value="Belum Menikah" v-model="form.status" />
                    <label class="custom-control-label" for="notMerried">Belum Menikah</label>
                  </div>
                </div>
                <small class="d-block text-danger mt-1">{{ error.status }}</small>
              </div>

              <div class="form-group mb-4">
                <label class="font-weight-bold text-dark-75 mb-3 d-block">Jenis Kelamin:</label>
                <div class="d-flex flex-wrap gap-2">
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="male" name="gender" class="custom-control-input" value="Laki-laki" v-model="form.gender" @change="clearBpjsAutofill('gender')" />
                    <label class="custom-control-label" for="male">Laki-laki</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="female" name="gender" class="custom-control-input" value="Perempuan" v-model="form.gender" @change="clearBpjsAutofill('gender')" />
                    <label class="custom-control-label" for="female">Perempuan</label>
                  </div>
                </div>
                <transition name="bpjs-note">
                  <small v-if="hasBpjsAutofill('gender')" class="bpjs-autofill-note">
                    <i class="fas fa-check-circle"></i> Jenis kelamin diisi otomatis dari data BPJS
                    <button type="button" aria-label="Tutup penanda" @click="clearBpjsAutofill('gender')">&times;</button>
                  </small>
                </transition>
                <small class="d-block text-danger mt-1">{{ error.gender }}</small>
              </div>

              <div class="form-group mb-0 patient-form-grid__wide">
                <label class="font-weight-bold text-dark-75 mb-3 d-block">Golongan Darah: <em class="text-muted font-weight-normal">opsional</em></label>
                <div class="d-flex flex-wrap gap-2">
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="A-" name="bloodType" class="custom-control-input" value="A-" v-model="form.blood_type" />
                    <label class="custom-control-label" for="A-">A-</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="A+" name="bloodType" class="custom-control-input" value="A+" v-model="form.blood_type" />
                    <label class="custom-control-label" for="A+">A+</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="B-" name="bloodType" class="custom-control-input" value="B-" v-model="form.blood_type" />
                    <label class="custom-control-label" for="B-">B-</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="B+" name="bloodType" class="custom-control-input" value="B+" v-model="form.blood_type" />
                    <label class="custom-control-label" for="B+">B+</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="O-" name="bloodType" class="custom-control-input" value="O-" v-model="form.blood_type" />
                    <label class="custom-control-label" for="O-">O-</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="O+" name="bloodType" class="custom-control-input" value="O+" v-model="form.blood_type" />
                    <label class="custom-control-label" for="O+">O+</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="AB-" name="bloodType" class="custom-control-input" value="AB-" v-model="form.blood_type" />
                    <label class="custom-control-label" for="AB-">AB-</label>
                  </div>
                  <div class="custom-control custom-radio custom-control-inline">
                    <input type="radio" id="AB+" name="bloodType" class="custom-control-input" value="AB+" v-model="form.blood_type" />
                    <label class="custom-control-label" for="AB+">AB+</label>
                  </div>
                </div>
                <small class="text-danger mt-1 d-block">{{ error.blood_type }}</small>
              </div>

            </div>
          </div>
          
          <div class="card card-custom border-0 shadow-sm rounded-xl mb-6">
            <div class="card-header bg-transparent border-0 pt-6 pb-0">
              <h5 class="card-title font-weight-bolder text-dark-75 mb-0">Data Medis Tambahan</h5>
            </div>
            <div class="card-body p-6">

          <!-- Input Alergic Type -->
          <b-form-group id="input-group-alergic">
            <label for="input-alergic"
              >Jenis Alergi: <em class="text-muted">opsional</em></label
            >
            <b-form-input
              id="input-alergic"
              v-model="form.allergy"
              placeholder="Alergi"
            >
            </b-form-input>
            <small class="text-danger">{{ error.allergy }}</small>
          </b-form-group>

          <!-- Input Parent Name -->
          <b-form-group id="input-group-parent-name">
            <label for="input-parent-name"
              >Nama Orang Tua: <em class="text-muted">opsional</em></label
            >
            <b-form-input
              id="input-parent-name"
              v-model="form.parent_name"
              placeholder="Nama Orang Tua"
            ></b-form-input>
            <small class="text-danger">{{ error.parent_name }}</small>
          </b-form-group>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Nomor Telepon Seluler:</label>
                    <b-form-input
                      id="input-mobile-phone"
                      v-model="form.mobile_phone"
                      placeholder="Telepon Seluler"
                      class="form-control-solid rounded-lg"
                      @input="clearBpjsAutofill('mobile_phone')"
                    ></b-form-input>
                    <transition name="bpjs-note">
                      <small v-if="hasBpjsAutofill('mobile_phone')" class="bpjs-autofill-note">
                        <i class="fas fa-check-circle"></i> Nomor HP diisi otomatis dari data BPJS
                        <button type="button" aria-label="Tutup penanda" @click="clearBpjsAutofill('mobile_phone')">&times;</button>
                      </small>
                    </transition>
                    <small class="text-danger mt-1 d-block">{{ error.mobile_phone }}</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Telepon: <em class="text-muted font-weight-normal">opsional</em></label>
                    <b-form-input
                      id="input-phone"
                      v-model="form.phone"
                      placeholder="Telepon"
                      class="form-control-solid rounded-lg"
                    ></b-form-input>
                    <small class="text-danger mt-1 d-block">{{ error.phone }}</small>
                  </div>
                </div>
              </div>

              <!-- Input Email -->
              <div class="form-group mb-4">
                <label class="font-weight-bold text-dark-75 mb-2">Email: <em class="text-muted font-weight-normal">opsional</em></label>
                <b-form-input
                  id="input-email"
                  v-model="form.email"
                  type="email"
                  placeholder="Email"
                  class="form-control-solid rounded-lg"
                ></b-form-input>
                <small class="text-danger mt-1 d-block">{{ error.email }}</small>
              </div>

            </div>
          </div>
          
          <div class="card card-custom border-0 shadow-sm rounded-xl mb-6">
            <div class="card-header bg-transparent border-0 pt-6 pb-0">
              <h5 class="card-title font-weight-bolder text-dark-75 mb-0">Alamat Tinggal</h5>
            </div>
            <div class="card-body p-6">

              <!-- Input Address -->
              <div class="form-group mb-4">
                <label class="font-weight-bold text-dark-75 mb-2">Alamat Lengkap:</label>
                <b-form-textarea
                  id="input-address"
                  v-model="form.address"
                  rows="3"
                  placeholder="Alamat Lengkap"
                  class="form-control-solid rounded-lg"
                ></b-form-textarea>
                <small class="text-danger mt-1 d-block">{{ error.address }}</small>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Negara: <em class="text-muted font-weight-normal">Default Indonesia</em></label>
                    <div class="patient-search-select-shell">
                      <span class="patient-search-select-shell__icon" aria-hidden="true"><i class="fas fa-globe-asia"></i></span>
                      <treeselect
                        class="patient-search-select hc-no-form-icons"
                        v-model="form.country_id"
                        :options="country"
                        placeholder="Pilih Negara"
                        no-results-text="Negara tidak ditemukan"
                        no-options-text="Data negara belum tersedia"
                        @input="countrySet"
                        required
                      />
                    </div>
                    <small class="text-danger mt-1 d-block">{{ error.country }}</small>
                  </div>
                </div>
                
                <div class="col-md-6" v-if="isCountry">
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Provinsi:</label>
                    <div class="patient-search-select-shell">
                      <span class="patient-search-select-shell__icon" aria-hidden="true"><i class="fas fa-map-marker-alt"></i></span>
                      <treeselect
                        class="patient-search-select hc-no-form-icons"
                        v-model="form.province_id"
                        :options="region"
                        placeholder="Pilih Provinsi"
                        no-results-text="Provinsi tidak ditemukan"
                        no-options-text="Pilih negara terlebih dahulu"
                        @input="regionSet"
                        required
                      />
                    </div>
                    <small class="text-danger mt-1 d-block">{{ error.province }}</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4" v-if="isRegion && isCountry">
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Kota / Kabupaten:</label>
                    <div class="patient-search-select-shell">
                      <span class="patient-search-select-shell__icon" aria-hidden="true"><i class="fas fa-city"></i></span>
                      <treeselect
                        class="patient-search-select hc-no-form-icons"
                        v-model="form.capital_id"
                        :options="regionChild"
                        placeholder="Pilih Kota/Kabupaten"
                        no-results-text="Kota atau kabupaten tidak ditemukan"
                        no-options-text="Pilih provinsi terlebih dahulu"
                        @select="capitalSet"
                        required
                      />
                    </div>
                    <small class="text-danger mt-1 d-block">{{ error.capital }}</small>
                  </div>
                </div>
                
                <div class="col-md-4" v-if="form.capital_id">
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Kecamatan:</label>
                    <div class="patient-search-select-shell">
                      <span class="patient-search-select-shell__icon" aria-hidden="true"><i class="fas fa-map-signs"></i></span>
                      <treeselect
                        class="patient-search-select hc-no-form-icons"
                        v-model="form.subdistrict_id"
                        :options="subdistricts"
                        placeholder="Pilih Kecamatan"
                        no-results-text="Kecamatan tidak ditemukan"
                        no-options-text="Pilih kota atau kabupaten terlebih dahulu"
                        @input="subdistrictSet"
                        required
                      />
                    </div>
                    <small class="text-danger mt-1 d-block">{{ error.subdistrict_id }}</small>
                  </div>
                </div>
                
                <div class="col-md-4" v-if="form.subdistrict_id">
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Kelurahan:</label>
                    <div class="patient-search-select-shell">
                      <span class="patient-search-select-shell__icon" aria-hidden="true"><i class="fas fa-home"></i></span>
                      <treeselect
                        class="patient-search-select hc-no-form-icons"
                        v-model="form.village_id"
                        :options="villages"
                        placeholder="Pilih Kelurahan"
                        no-results-text="Kelurahan tidak ditemukan"
                        no-options-text="Pilih kecamatan terlebih dahulu"
                        required
                      />
                    </div>
                    <small class="text-danger mt-1 d-block">{{ error.village_id }}</small>
                  </div>
                </div>
              </div>

              <div class="row" v-if="source !== 'appointment'">
                <div class="col-md-6">
                  <!-- Input Doctor -->
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Dokter Yang Menangani: 
                      <em class="text-muted font-weight-normal" v-if="source != 'appointment' && source != 'payment' && source != 'casemanager' && source != 'prescriptions'">opsional</em>
                    </label>
                    <div class="patient-search-select-shell">
                      <span class="patient-search-select-shell__icon" aria-hidden="true"><i class="fas fa-user-md"></i></span>
                      <treeselect
                        class="patient-search-select hc-no-form-icons"
                        v-model="form.doctor_in_charge"
                        :multiple="true"
                        :options="doctors"
                        placeholder="Pilih Dokter"
                        no-results-text="Dokter tidak ditemukan"
                        no-options-text="Data dokter belum tersedia"
                        :required="source == 'appointment' || source == 'payment' || source == 'casemanager' || source == 'prescriptions' ? true : false"
                      />
                    </div>
                    <small class="text-danger mt-1 d-block">{{ error.doctor_in_charge }}</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <!-- Input Responsible Doctor -->
                  <div class="form-group mb-4">
                    <label class="font-weight-bold text-dark-75 mb-2">Dokter Penanggung Jawab (DPJP): <em class="text-muted font-weight-normal">opsional</em></label>
                    <div class="patient-search-select-shell">
                      <span class="patient-search-select-shell__icon" aria-hidden="true"><i class="fas fa-user-shield"></i></span>
                      <treeselect
                        class="patient-search-select hc-no-form-icons"
                        v-model="form.responsible_doctor"
                        :multiple="true"
                        :options="doctors"
                        placeholder="Pilih Dokter DPJP"
                        no-results-text="Dokter tidak ditemukan"
                        no-options-text="Data dokter belum tersedia"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Input Description -->
              <div class="form-group mb-0">
                <label class="font-weight-bold text-dark-75 mb-2">Catatan: <em class="text-muted font-weight-normal">opsional</em></label>
                <b-form-textarea
                  id="input-description"
                  v-model="form.notes"
                  rows="3"
                  placeholder="Catatan tambahan pasien"
                  class="form-control-solid rounded-lg"
                ></b-form-textarea>
                <small class="text-danger mt-1 d-block">{{ error.notes }}</small>
              </div>

            </div>
          </div>

          <div class="d-flex align-items-center mt-8 pb-4">
            <b-button 
              type="submit" 
              class="btn-modern-submit"
              data-icon-contrast="light"
            >
              <i class="fas fa-save mr-2"></i> Simpan
            </b-button>
            <b-button
              class="btn-modern-cancel ml-4"
              v-if="purpose != 'modal'"
              @click="$router.push('/patient/list')"
            >
              Batal
            </b-button>
          </div>
        </b-form>
      </div>
    </div>
  </v-app>
</template>

<script>
import saasModule from "@/core/modules/CrudSaasModule.js";
import module from "@/core/modules/CrudModule.js";
import validation from "@/core/modules/ValidationModule.js";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
// import webCam from 'vue-web-cam'
import { WebCam } from "vue-web-cam";
import moduleBridge from "@/core/modules/CrudModuleBridge.js";
import ApiService from "@/core/services/api.service";
import { errorMessage, unwrap } from "@/view/pages/bpjs/bpjs.helpers";
import {
  getSsOrganizationId,
  getUser as getCurrentUser,
} from "@/core/services/jwt.service.js";

export default {
  name: "Form",

  components: {
    Treeselect,
    "vue-web-cam": WebCam,
  },

  props: {
    form: Object,
    route: String,
    purpose: String,
    password: Boolean,
    edit: Boolean,
    source: {
      type: String,
      default: "",
    },
    showBpjsRegistration: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ss_organization_id: getSsOrganizationId(),
      currentUser: getCurrentUser(),
      error: {
        email: "",
        name: "",
        birt_place: "",
        birt_date: "",
        address: "",
        gender: "",
        phone: "",
        mobile_phone: "",
        parent_name: "",
        blood_type: "",
        id_card_number: "",
        password: "",
        password_confirmation: "",
        allergy: "",
        title: "",
        status: "",
        doctor_in_charge: "",
        photo: "",
        patient_id: "",
        notes: "",
        country: "",
        region: "",
        capital: "",
        subdistrict_id: "",
        village_id: "",
      },
      doctors: [],
      country: [],
      region: [],
      regionChild: [],
      subdistricts: [],
      villages: [],
      users: null,
      imgLoaded: false,
      uploadedImageUrl: "",
      uploadMethod: "upload",
      photoDragging: false,
      // camera
      baseImg: null,
      camera: null,
      deviceId: null,
      devices: [],
      img: null,
      captureStatus: false,

      // other
      isCountry: true,
      isRegion: false,

      // date
      time: null,
      menu2: false,
      modal2: false,
      picker: window.medhopToday(),

      // other
      isRegion: false,
      textPatientId: "0",
      ssPatientIdStatus: false,
      bpjsLookupType: "nokartu",
      bpjsLookupNumber: "",
      bpjsLoading: false,
      bpjsVerified: false,
      bpjsParticipant: {},
      bpjsError: "",
      bpjsAutofilledFields: {},
      bpjsAutofillTimer: null,
    };
  },

  methods: {
    setBpjsLookupType(type) {
      if (this.bpjsLookupType === type) return;
      this.bpjsLookupType = type;
      this.bpjsLookupNumber = "";
      this.resetBpjsVerification();
    },
    hasBpjsAutofill(field) {
      return Boolean(this.bpjsAutofilledFields[field]);
    },
    clearBpjsAutofill(field) {
      if (field) {
        this.$delete(this.bpjsAutofilledFields, field);
        return;
      }
      this.bpjsAutofilledFields = {};
      if (this.bpjsAutofillTimer) {
        clearTimeout(this.bpjsAutofillTimer);
        this.bpjsAutofillTimer = null;
      }
    },
    markBpjsAutofill(fields) {
      this.clearBpjsAutofill();
      fields.forEach(field => this.$set(this.bpjsAutofilledFields, field, true));
      this.bpjsAutofillTimer = setTimeout(() => {
        this.bpjsAutofilledFields = {};
        this.bpjsAutofillTimer = null;
      }, 8000);
    },
    coverageChanged() {
      this.bpjsError = "";
      if (this.form.coverage_type !== "bpjs") {
        this.resetBpjsVerification();
        this.form.bpjs_card_number = "";
        this.form.bpjs_participant_status = "";
        this.form.bpjs_verified_at = "";
        this.form.bpjs_fktp_code = "";
        this.form.bpjs_fktp_name = "";
      }
    },
    onBpjsNumberInput() {
      this.bpjsLookupNumber = String(this.bpjsLookupNumber || "").replace(/\D/g, "");
      this.resetBpjsVerification();
    },
    resetBpjsVerification() {
      this.bpjsVerified = false;
      this.bpjsParticipant = {};
      this.bpjsError = "";
      this.clearBpjsAutofill();
      this.form.bpjs_participant_status = "";
      this.form.bpjs_verified_at = "";
      this.form.bpjs_fktp_code = "";
      this.form.bpjs_fktp_name = "";
    },
    normalizeBpjsDate(value) {
      const raw = String(value || "").trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      const match = raw.match(/^(\d{2})[-\/]?(\d{2})[-\/]?(\d{4})$/);
      return match ? `${match[3]}-${match[2]}-${match[1]}` : "";
    },
    applyBpjsParticipant(participant) {
      const statusObject = participant.statusPeserta || participant.pstSts || {};
      const status = typeof statusObject === "object"
        ? statusObject.keterangan || statusObject.nama || statusObject.status || ""
        : statusObject;
      if (String(status).trim().toUpperCase() !== "AKTIF") {
        throw new Error(`Status peserta ${status || "tidak aktif"}. Pendaftaran BPJS tidak dapat dilanjutkan.`);
      }

      const ppk = participant.ppk || participant.provUmum || {};
      const autofilledFields = [];
      const participantName = participant.nama || participant.name;
      const participantNik = participant.nik || participant.NIK || participant.noKTP;
      const participantBirthDate = this.normalizeBpjsDate(participant.tglLahir || participant.tanggalLahir);
      const participantPhone = participant.noHP || participant.noHp || participant.noTelepon;

      if (participantName) {
        this.form.name = participantName;
        autofilledFields.push("name");
      }
      if (participantNik) {
        this.form.id_card_number = participantNik;
        autofilledFields.push("id_card_number");
      }
      if (participantBirthDate) {
        this.form.birt_date = participantBirthDate;
        autofilledFields.push("birt_date");
      }
      if (participantPhone) {
        this.form.mobile_phone = participantPhone;
        autofilledFields.push("mobile_phone");
      }

      const sex = String(participant.sex || participant.jenisKelamin || "").toUpperCase();
      if (sex === "L" || sex === "LAKI-LAKI" || sex === "MALE") {
        this.form.gender = "Laki-laki";
        autofilledFields.push("gender");
      } else if (sex === "P" || sex === "PEREMPUAN" || sex === "FEMALE") {
        this.form.gender = "Perempuan";
        autofilledFields.push("gender");
      }

      this.form.bpjs_card_number = String(participant.noKartu || participant.noKartuBpjs || this.bpjsLookupNumber);
      this.form.bpjs_participant_status = String(status).toUpperCase();
      this.form.bpjs_verified_at = new Date().toISOString();
      this.form.bpjs_fktp_code = ppk.kdPPK || ppk.kode || ppk.kdProvider || "";
      this.form.bpjs_fktp_name = ppk.nmPPK || ppk.nama || ppk.nmProvider || "";
      this.bpjsParticipant = participant;
      this.bpjsVerified = true;
      this.markBpjsAutofill(autofilledFields);
    },
    async lookupBpjsParticipant() {
      const number = String(this.bpjsLookupNumber || "").replace(/\D/g, "");
      this.bpjsLookupNumber = number;
      this.resetBpjsVerification();
      if (number.length < 8 || number.length > 20) {
        this.bpjsError = "Masukkan 8–20 digit nomor kartu BPJS atau NIK.";
        return;
      }

      this.bpjsLoading = true;
      try {
        const response = await ApiService.get(`bpjs/peserta/${this.bpjsLookupType}/${number}`);
        if (!response || (response.status && response.status >= 400)) {
          throw response || new Error("Layanan BPJS tidak dapat dihubungi.");
        }
        const value = unwrap(response);
        const participant = value && value.peserta ? value.peserta : value;
        if (!participant || typeof participant !== "object") {
          throw new Error("Data peserta BPJS tidak ditemukan.");
        }
        this.applyBpjsParticipant(participant);
      } catch (error) {
        this.bpjsError = errorMessage(error, error.message || "Data peserta BPJS tidak ditemukan.");
      } finally {
        this.bpjsLoading = false;
      }
    },
    resetSsPatientId() {
      this.form.ss_patient_id = "";
      this.ssPatientIdStatus = false;
    },
    async satuSehatConnect() {
      if (this.form.id_card_number == "") {
        Swal.fire({
          title: "Gagal",
          text: "Harap isi form Nomor KTP terlebih dahulu",
          icon: "error",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
        });

        throw new Error("Validation");
      }
      SwalLoading.fire();
      let patient = await moduleBridge.get(
        `patient?identifier=${this.form.id_card_number}`
      );
      if (patient == null) {
        Swal.fire({
          title: "Gagal",
          text: "Pasien tidak terdaftar, periksa kembali Nomor KTP",
          icon: "error",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
        });

        this.ssPatientIdStatus = false;
      } else {
        Swal.fire({
          title: "Berhasil",
          text: "Pasien telah terdaftar di satu sehat",
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
        });
        this.form.ss_patient_id = patient.entry[0].resource.id;
        this.ssPatientIdStatus = true;
      }
    },

    formaterDate(val) {
      if (val.length == 4) {
        this.form.birt_date = this.form.birt_date + "-";
      }
      if (val.length == 7) {
        this.form.birt_date = this.form.birt_date + "-";
      }
    },

    setGender() {
      if (this.form.title == "Tn") {
        this.form.gender = "Laki-laki";
      } else if (this.form.title != "Tn" && this.form.title != "An") {
        this.form.gender = "Perempuan";
      }
    },

    regionSet(data) {
      if (data == null) {
        this.isRegion = false;
        this.form.capital_id = null;
        this.form.subdistrict_id = null;
        this.form.village_id = null;
        return;
      }

      let status = this.getRegionChildOptions(data);
      this.form.capital_id = null;
      this.form.subdistrict_id = null;
      this.form.village_id = null;

      if (status) {
        this.isRegion = true;
      }
    },

    countrySet(data) {
      if (data == 74) {
        this.isCountry = true;
      } else {
        this.isCountry = false;
      }

      this.form.province_id  = null
      this.form.capital_id  = null
      this.form.subdistrict_id = null
      this.form.village_id = null
    },

    capitalSet(data) {
      this.form.subdistrict_id = null
      this.form.village_id = null
      if (!data || !data.code_id) {
        this.subdistricts = [];
        return;
      }
      this.getSubdistrict(data.code_id);
    },

    onBirthDateInput(value) {
      this.clearBpjsAutofill("birt_date");
      this.formaterDate(value || "");
    },

    subdistrictSet(data) {
      this.form.village_id = null
      if (!data) {
        this.villages = [];
        return;
      }
      this.getVillage(data);
    },

    async getCountryOptions() {
      let response = await module.setTreeSelect("countries");
      // Success
      if (response.state == "success") {
        // Set Option Select
        this.country = response.data;
        if (this.form.country_id == null) {
          const indonesia = this.country.find((country) => country.id == 74);
          this.form.country_id = indonesia ? indonesia.id : null;
        }
      }

      if (this.form.country_id == 74) {
        //74 is indonesia country id in database
        this.isCountry = true;
      } else {
        this.isCountry = false;
      }
    },

    async getRegionOptions() {
      let response = await module.setTreeSelect("regions");
      // Success
      if (response.state == "success") {
        // Set Option Select
        this.region = response.data;
      }

      if (this.form.province_id != null) {
        this.isRegion = true;
        this.getRegionChildOptions(this.form.province_id);
      }

      if(this.form.capital_id != null){
        this.getSubdistrict(this.form.capital_code)
      }

      if(this.form.subdistrict_id != null){
        this.getVillage(this.form.subdistrict_id)
      }
    },

    async getRegionChildOptions(id) {
      let response = await module.setTreeSelectProvince("region/" + id);
      // Success
      if (response.state == "success") {
        // Set Option Select
        this.regionChild = response.data;
      }

      return true;
    },

    async getSubdistrict(code_id) {
      let response = await module.setTreeSelect(`regions/subdistrict?code_id=${code_id}`);
      // Success
      if (response.state == "success") {
        // Set Option Select
        this.subdistricts = response.data;
      }

      return true;
    },

    async getVillage(code_id) {
      let response = await module.setTreeSelect(`regions/village?code_id=${code_id}`);
      // Success
      if (response.state == "success") {
        // Set Option Select
        this.villages = response.data;
      }

      return true;
    },

    resetImg() {
      if (this.uploadedImageUrl.startsWith("blob:"))
        URL.revokeObjectURL(this.uploadedImageUrl);
      if (this.$refs.webcam && typeof this.$refs.webcam.stop === "function")
        this.$refs.webcam.stop();
      this.form.isResetImg = true;
      this.form.photo = [];
      this.form.photo_name = s3Endpoint + this.form.photo_default;
      this.uploadedImageUrl = "";
      this.img = null;
      this.baseImg = null;
      this.captureStatus = false;
      this.photoDragging = false;
      this.uploadMethod = "upload";
      if (this.$refs.photoInput) this.$refs.photoInput.value = "";
    },

    async checkPatientId(number) {
      if (number == "") {
        number = 0;
      }

      let response = await module.get("patients-number/" + number);

      if (response == 1) {
        this.textPatientId = "1";
      } else {
        this.textPatientId = "0";
      }
    },

    onImageLoad() {
      this.imgLoaded = true;
    },

    openPhotoPicker() {
      if (this.$refs.photoInput) this.$refs.photoInput.click();
    },

    onPhotoInput(event) {
      const file = event.target.files && event.target.files[0];
      if (event.target) event.target.value = "";
      this.preparePhotoFile(file);
    },

    onFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      this.preparePhotoFile(file);
    },

    dropPhoto(event) {
      this.photoDragging = false;
      const file =
        event.dataTransfer &&
        event.dataTransfer.files &&
        event.dataTransfer.files[0];
      this.preparePhotoFile(file);
    },

    leavePhotoDropzone(event) {
      if (!event.currentTarget.contains(event.relatedTarget))
        this.photoDragging = false;
    },

    preparePhotoFile(file) {
      this.error.photo = "";
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.error.photo = "Format foto harus JPG atau PNG.";
        return;
      }
      if (file.size > 4 * 1024 * 1024) {
        this.error.photo = "Ukuran foto maksimal 4 MB.";
        return;
      }
      if (this.uploadedImageUrl.startsWith("blob:"))
        URL.revokeObjectURL(this.uploadedImageUrl);
      this.uploadedImageUrl = URL.createObjectURL(file);
      this.form.photo = file;
      this.form.isResetImg = false;
      this.img = null;
      this.baseImg = null;
      this.captureStatus = false;
      this.uploadMethod = "upload";
    },

    formatFileSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },

    oncancel() {
      if (this.purpose == "master") {
        // this.onStop()
        this.$router.push("/patient/list");
      } else if (this.purpose == "modal") {
        // this.onStop()
        $bvModal.hide("modal-patient");
      }
    },

    changeUploadBtn() {
      if (this.uploadMethod == "upload") {
        this.uploadMethod = "camera";
        this.$nextTick(() => {
          if (this.$refs.webcam && typeof this.$refs.webcam.start === "function")
            this.$refs.webcam.start();
        });
      } else {
        if (this.$refs.webcam && typeof this.$refs.webcam.stop === "function")
          this.$refs.webcam.stop();
        this.uploadMethod = "upload";
      }
    },

    retakePhoto() {
      this.captureStatus = false;
      this.baseImg = null;
      this.img = null;
      this.$nextTick(() => {
        if (this.$refs.webcam && typeof this.$refs.webcam.start === "function")
          this.$refs.webcam.start();
      });
    },

    async getDoctorOptions() {
      let response = await module.setTreeSelect("doctors");
      // Success
      if (response.state == "success") {
        // Set Option Select
        this.doctors = response.data;
        const currentDoctor =
          this.currentUser && this.currentUser.user_type_id == 1
            ? this.doctors.find(
                (doctor) => doctor.id == this.currentUser.id
              )
            : null;

        if (
          !this.edit &&
          currentDoctor &&
          Array.isArray(this.form.doctor_in_charge) &&
          this.form.doctor_in_charge.length == 0
        ) {
          this.form.doctor_in_charge = [currentDoctor.id];
        }
      }
    },

    async getUser() {
      let response = await module.setSelectOption("users");
      // Success
      if (response.state == "success") {
        // Set Option Select
        this.users = response.data;
        this.users.unshift({ text: "Pilih User", value: "", disabled: true });
      }
    },

    async formOnSubmit() {
      if (this.textPatientId == "0") {
        if (this.showBpjsRegistration && this.form.coverage_type === "bpjs" && !this.bpjsVerified) {
          Swal.fire({
            title: "Verifikasi BPJS diperlukan",
            text: "Cek nomor kartu atau NIK terlebih dahulu dan pastikan status peserta aktif.",
            icon: "warning",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 5000,
          });
          return;
        }
        const requiredFields = {
          title: "Gelar",
          name: "Nama lengkap",
          birt_date: "Tanggal lahir",
          gender: "Jenis kelamin",
          mobile_phone: "Nomor telepon seluler",
          address: "Alamat lengkap",
        };
        const missingFields = Object.entries(requiredFields).filter(
          ([key]) =>
            this.form[key] === null ||
            typeof this.form[key] === "undefined" ||
            String(this.form[key]).trim() === ""
        );

        validation.clearValidationError(this.error);
        missingFields.forEach(([key, label]) => {
          this.error[key] = `${label} tidak boleh kosong`;
        });

        if (missingFields.length > 0) {
          Swal.fire({
            title: "Data belum lengkap",
            text: `Lengkapi: ${missingFields
              .map(([, label]) => label)
              .join(", ")}`,
            icon: "warning",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 5000,
            timerProgressBar: true,
          });
          return;
        }

        if (this.img != null) {
          this.form.photo = this.img;
        }

        let formData = new FormData();
        for (const [key, value] of Object.entries(this.form)) {
          let formValue = value;
          if (key == "doctor_in_charge" || key == "responsible_doctor") {
            formValue = JSON.stringify(Array.isArray(value) ? value : []);
          } else if (key == "gender") {
            formValue =
              String(value).toLowerCase() == "laki-laki" ||
              String(value).toLowerCase() == "male"
                ? "male"
                : "female";
          }
          if (formValue === null || typeof formValue === "undefined") {
            formValue = "";
          }
          formData.append(key, formValue);
        }
        // Make Request
        let response = await module.submit(formData, this.route);

        // Check Response
        if (response.state == "error") {
          // Validation Error
          if (response.error != "") {
            this.error = validation.setValidationError(
              this.error,
              response.error.errors
            );
          } else {
            Swal.fire(
              "Gagal",
              "Terjadi kesalahan sistem, silahkan hubungi tim pengembang",
              "error"
            );
          }
        } else if (response.state == "failed") {
          Swal.fire(
            "Gagal",
            "Terjadi kesalahan sistem, silahkan hubungi tim pengembang",
            "error"
          );
        } else {
          // Success
          this.$root.$emit("UpdateNewPatient");
          if (response.success.title != "" && response.success.message != "") {
            if (this.purpose == "master") {
              Swal.fire(
                response.success.title,
                response.success.message,
                "success"
              );
              this.$router.push("/patient/list");
            } else if (this.purpose == "modal") {
              const responseItem = response.success.item || null;
              const savedPatient = responseItem
                ? responseItem.original || responseItem.data || responseItem
                : null;

              if (!savedPatient || !savedPatient.id) {
                Swal.fire(
                  "Gagal",
                  "Pasien belum berhasil disimpan. Silakan periksa kembali data negara dan wilayah.",
                  "error"
                );
                return;
              }

              this.$emit("submitPatient", {
                id: savedPatient.id,
                name:
                  savedPatient.name +
                  ` (${savedPatient.mobile_phone || savedPatient.phone || "-"})`,
                id_card_number: savedPatient.id_card_number || "",
              });
            } else {
              Swal.fire(
                response.success.title,
                response.success.message,
                "success"
              );
            }
          } else {
            Swal.fire("Gagal", "Data Gagal Disimpan", "error");
          }
        }
      } else {
        Swal.fire(
          "Perhatian",
          "Nomor pasien telah digunakan, silahkan masukkan nomor lain",
          "warning"
        );
      }
    },

    // camera method
    onCapture() {
      this.baseImg = this.$refs.webcam.capture();
      this.img = this.dataURItoBlob(this.baseImg);
      this.captureStatus = true;
      this.onStop();
    },
    onStarted(stream) {},
    onStopped(stream) {},
    onStop() {
      this.$refs.webcam.stop();
    },
    onPause() {
      this.$refs.webcam.pause();
    },
    onResume() {
      this.$refs.webcam.resume();
    },
    onStart() {
      this.$refs.webcam.start();
    },
    onError(error) {},
    onCameras(cameras) {
      this.devices = cameras;
    },
    onCameraChange(deviceId) {
      this.deviceId = deviceId;
      this.camera = deviceId;
    },

    // convert base64 to image
    dataURItoBlob(dataURI) {
      var byteString;
      if (dataURI.split(",")[0].indexOf("base64") >= 0)
        byteString = atob(dataURI.split(",")[1]);
      else byteString = unescape(dataURI.split(",")[1]);

      // separate out the mime component
      var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

      // write the bytes of the string to a typed array
      var ia = new Uint8Array(byteString.length);
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new Blob([ia], {
        type: mimeString,
      });
    },
  },

  computed: {
    selectedPhotoFile() {
      const photo = this.form && this.form.photo;
      return photo && typeof photo.name === "string" ? photo : null;
    },
    profilePhotoPreview() {
      return (
        this.uploadedImageUrl ||
        (this.captureStatus && this.baseImg) ||
        this.form.photo_name ||
        ""
      );
    },
    hasProfilePhoto() {
      if (
        this.selectedPhotoFile ||
        this.captureStatus ||
        this.uploadedImageUrl.startsWith("blob:")
      )
        return true;
      const current = String(this.form.photo_name || "");
      return Boolean(
        current &&
          !this.form.isResetImg &&
          !current.includes("default-profile") &&
          !current.includes("blank.png")
      );
    },
    device: function () {
      return this.devices.find((n) => n.deviceId === this.deviceId);
    },
  },

  watch: {
    camera: function (id) {
      this.deviceId = id;
    },
    devices: function () {
      // Once we have a list select the first one
      const [first, ...tail] = this.devices;
      if (first) {
        this.camera = first.deviceId;
        this.deviceId = first.deviceId;
      }
    },
  },

  mounted() {
    this.getUser();
    this.getDoctorOptions();
    this.getCountryOptions();
    this.getRegionOptions();
  },
  beforeDestroy() {
    if (this.bpjsAutofillTimer) clearTimeout(this.bpjsAutofillTimer);
    if (this.uploadedImageUrl.startsWith("blob:"))
      URL.revokeObjectURL(this.uploadedImageUrl);
  },
};
</script>

<style scoped>
.patient-photo-card {
  overflow: hidden;
  border: 1px solid #dce8ef !important;
  background: #fff;
}

.patient-photo-card__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.patient-photo-card__heading span {
  display: block;
  margin-bottom: 3px;
  color: #1e958f;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.09em;
}

.patient-photo-card__heading h5 {
  margin: 0;
  color: #213950;
  font-size: 1rem;
  font-weight: 800;
}

.patient-photo-card__heading small {
  padding: 4px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #718096;
  font-size: 0.65rem;
  font-weight: 700;
}

.patient-photo-dropzone,
.patient-camera-stage {
  position: relative;
  display: grid;
  width: 100%;
  overflow: hidden;
  place-items: center;
  aspect-ratio: 1 / 1;
  border-radius: 15px;
}

.patient-photo-dropzone {
  border: 2px dashed #9bcfc9;
  background: linear-gradient(145deg, #f5fcfb, #f8fafc);
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease,
    transform 0.16s ease, box-shadow 0.16s ease;
}

.patient-photo-dropzone:hover,
.patient-photo-dropzone:focus-visible,
.patient-photo-dropzone.is-dragging {
  border-color: #118b83;
  background: #ecfaf7;
  outline: none;
  box-shadow: 0 0 0 4px rgba(30, 149, 143, 0.12);
}

.patient-photo-dropzone.is-dragging {
  transform: scale(0.985);
}

.patient-photo-dropzone.has-photo {
  border-style: solid;
  border-color: #d4e2e8;
  background: #eaf1f5;
}

.patient-photo-dropzone > input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.patient-photo-dropzone > img,
.patient-camera-stage > img,
.patient-camera-stage ::v-deep video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.patient-photo-dropzone__empty {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 18px;
  text-align: center;
}

.patient-photo-dropzone__empty > span {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;
  border-radius: 15px;
  background: #dff5f1;
  color: #0f7d76;
  font-size: 1.2rem;
}

.patient-photo-dropzone__empty strong {
  margin: 12px 0 3px;
  color: #213950;
  font-size: 0.82rem;
}

.patient-photo-dropzone__empty small {
  color: #60758a;
  font-size: 0.7rem;
  line-height: 1.45;
}

.patient-photo-dropzone__empty em {
  margin-top: 7px;
  color: #93a2b2;
  font-size: 0.62rem;
  font-style: normal;
}

.patient-photo-dropzone__overlay {
  position: absolute;
  inset: auto 0 0;
  padding: 24px 10px 10px;
  background: linear-gradient(transparent, rgba(15, 39, 57, 0.86));
  color: #fff;
  font-size: 0.67rem;
  font-weight: 800;
  text-align: center;
}

.patient-photo-dropzone__overlay i {
  margin-right: 5px;
  color: inherit !important;
}

.patient-camera-stage {
  border: 1px solid #2d4859;
  background: #102a3a;
}

.patient-photo-meta {
  min-height: 58px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  padding: 10px 2px 6px;
  color: #60758a;
  font-size: 0.67rem;
}

.patient-photo-meta span {
  color: #16867f;
  font-weight: 800;
}

.patient-photo-meta span.is-muted {
  color: #8796a6;
  font-weight: 650;
}

.patient-photo-meta strong {
  overflow: hidden;
  color: #2f465c;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.patient-photo-meta i {
  margin-right: 4px;
  color: inherit !important;
}

.patient-photo-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.82fr) auto;
  gap: 7px;
}

.patient-photo-actions button {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 10px;
  border: 0;
  border-radius: 9px;
  font-size: 0.68rem;
  font-weight: 800;
  white-space: nowrap;
}

.patient-photo-actions__primary {
  border-color: transparent !important;
  background-color: #176f89 !important;
  background-image: linear-gradient(135deg, #17628b, #168e87) !important;
  color: #fff !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.patient-photo-actions__primary i,
.btn-modern-submit i {
  color: inherit !important;
  background-image: none !important;
  -webkit-background-clip: border-box !important;
  background-clip: border-box !important;
  -webkit-text-fill-color: currentColor !important;
}

.patient-photo-actions__secondary {
  background: #e9f5f3;
  color: #137a74;
}

.patient-photo-actions__danger {
  width: 38px;
  padding: 0 !important;
  background: #fff0f1;
  color: #b6414e;
}

.patient-photo-actions button:focus-visible {
  outline: 3px solid rgba(30, 149, 143, 0.2);
  outline-offset: 2px;
}

.patient-photo-error {
  display: block;
  margin-top: 8px;
  color: #b42333;
  font-size: 0.68rem;
  font-weight: 700;
}

.patient-coverage-card {
  overflow: hidden;
  border: 1px solid #dce8ef;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 28px rgba(20, 50, 78, 0.06);
}

.patient-coverage-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px;
}

.patient-coverage-eyebrow {
  margin-bottom: 4px;
  color: #1e958f;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.patient-coverage-heading h5 {
  color: #172b45;
  font-weight: 800;
}

.patient-coverage-heading p {
  color: #77869a;
}

.patient-coverage-options {
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
}

.patient-coverage-option {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 170px;
  min-height: 68px;
  gap: 12px;
  margin: 0;
  padding: 12px 16px;
  border: 1px solid #dce5ee;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
}

.patient-coverage-option.active {
  border-color: #1e958f;
  background: #f1fbf9;
  box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.1);
}

.patient-coverage-option input {
  position: absolute;
  opacity: 0;
}

.coverage-radio {
  width: 18px;
  height: 18px;
  border: 2px solid #becbd8;
  border-radius: 50%;
  box-shadow: inset 0 0 0 4px #fff;
}

.patient-coverage-option.active .coverage-radio {
  border-color: #1e958f;
  background: #1e958f;
}

.patient-coverage-option i {
  width: 34px;
  color: #1e958f;
  font-size: 1.35rem;
  text-align: center;
}

.patient-coverage-option strong,
.patient-coverage-option small {
  display: block;
}

.patient-coverage-option strong {
  color: #17314f;
  font-size: 0.98rem;
}

.patient-coverage-option small {
  margin-top: 2px;
  color: #8795a7;
  font-weight: 500;
}

.bpjs-lookup-panel {
  padding: 18px 24px 20px;
  border-top: 1px solid #e3edf2;
  background: #f8fcfb;
}

.bpjs-lookup-type,
.bpjs-input-shell,
.bpjs-lookup-button {
  height: 52px;
  border-radius: 12px !important;
}

.bpjs-lookup-type {
  display: grid;
  grid-template-columns: 1fr 0.55fr;
  gap: 4px;
  padding: 4px;
  border: 1px solid #d5e1ea;
  background: #fff;
}

.bpjs-lookup-type button {
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #718197;
  font-size: 0.93rem;
  font-weight: 650;
}

.bpjs-lookup-type button.active {
  background: #e7f7f4;
  color: #147f79;
}

.bpjs-input-shell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  border: 1px solid #d5e1ea;
  background: #fff;
}

.bpjs-input-shell:focus-within {
  border-color: #1e958f;
  box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.12);
}

.bpjs-input-shell i {
  color: #1e958f;
  font-size: 1.15rem;
}

.bpjs-input-shell input {
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #172b45;
  font-size: 1rem;
  font-weight: 500;
}

.bpjs-input-shell input::placeholder {
  color: #9aa9ba;
  font-weight: 400;
}

.bpjs-lookup-button {
  border: 0;
  background: linear-gradient(135deg, #1e4f7d, #1e958f);
  color: #fff;
  font-weight: 750;
}

.bpjs-lookup-button:disabled {
  opacity: 0.65;
}

.bpjs-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 15px;
  border-radius: 12px;
}

.bpjs-message.is-success {
  border: 1px solid #bce6dd;
  background: #eaf8f4;
  color: #126b64;
}

.bpjs-message.compact {
  display: inline-flex;
  max-width: 100%;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 0.88rem;
}

.bpjs-message.compact strong,
.bpjs-message.compact span {
  display: inline;
}

.bpjs-message.compact span::before {
  content: " · ";
}

.bpjs-message.is-error {
  border: 1px solid #f2c6c9;
  background: #fff1f2;
  color: #a93c45;
}

.bpjs-message i {
  margin-top: 2px;
  font-size: 1.15rem;
}

.bpjs-message strong,
.bpjs-message span {
  display: block;
}

.bpjs-message span {
  margin-top: 2px;
  font-weight: 500;
}

.bpjs-autofill-note {
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin-top: 7px;
  color: #13867f;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
}

.bpjs-autofill-note i {
  font-size: 0.8rem;
}

.bpjs-autofill-note button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  margin-left: 2px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #dff3ef;
  color: #13867f;
  font-size: 1rem;
  line-height: 1;
}

.bpjs-note-enter-active,
.bpjs-note-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.bpjs-note-enter,
.bpjs-note-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

@media (max-width: 991px) {
  .patient-coverage-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .patient-coverage-options {
    width: 100%;
  }

  .patient-coverage-option {
    flex: 1 1 0;
  }
}

@media (max-width: 575px) {
  .patient-coverage-options {
    flex-direction: column;
  }

  .patient-coverage-option {
    width: 100%;
  }
}

/* Fix Treeselect Double Borders (Global input styles leaking into treeselect) */
::v-deep .vue-treeselect__control {
  border: 1px solid #E7EDF4 !important;
  border-radius: 0.5rem;
  box-shadow: none !important;
  background-color: #FFFFFF !important;
}

::v-deep .vue-treeselect--focused .vue-treeselect__control {
  outline: 3px solid rgba(30, 149, 143, 0.24) !important;
  outline-offset: 0px !important;
  border-color: #1E958F !important;
}

::v-deep .vue-treeselect input {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

/* Searchable selects in the address and doctor section share one stable
   geometry in display, search, open, and multi-value states. */
.patient-search-select-shell {
  position: relative;
  width: 100%;
  min-width: 0;
}

.patient-search-select-shell__icon {
  position: absolute;
  z-index: 4;
  top: 7px;
  left: 8px;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 1px solid #d1e9e5;
  border-radius: 9px;
  background: #edf8f6;
  color: #16867f;
  font-size: 13px;
  line-height: 1;
  pointer-events: none;
  transition: border-color 0.15s ease, background 0.15s ease,
    color 0.15s ease;
}

.patient-search-select-shell__icon i {
  color: inherit !important;
  -webkit-text-fill-color: currentColor !important;
}

.patient-search-select-shell:focus-within .patient-search-select-shell__icon {
  border-color: #a9dcd6;
  background: #dff5f1;
  color: #0f766e;
}

::v-deep .patient-search-select {
  width: 100%;
  min-width: 0;
  color: #2f4358;
  font-family: inherit;
  font-size: 13px;
}

::v-deep .patient-search-select .vue-treeselect__control {
  width: 100%;
  height: 48px !important;
  min-height: 48px !important;
  padding: 0 4px !important;
  overflow: hidden;
  border: 1px solid #d5e1ea !important;
  border-radius: 11px !important;
  background: #fff !important;
  box-shadow: none !important;
}

::v-deep .patient-search-select.vue-treeselect--multi .vue-treeselect__control {
  height: auto !important;
  min-height: 48px !important;
}

::v-deep .patient-search-select.vue-treeselect--focused .vue-treeselect__control,
::v-deep .patient-search-select.vue-treeselect--open .vue-treeselect__control {
  border-color: #1e958f !important;
  outline: 0 !important;
  box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.13) !important;
}

::v-deep .patient-search-select .vue-treeselect__value-container {
  height: 46px !important;
  min-height: 46px !important;
  padding: 0 8px 0 48px !important;
  vertical-align: middle;
}

::v-deep .patient-search-select.vue-treeselect--multi .vue-treeselect__value-container {
  height: auto !important;
  min-height: 46px !important;
  padding: 3px 8px 3px 48px !important;
}

::v-deep .patient-search-select .vue-treeselect__placeholder,
::v-deep .patient-search-select .vue-treeselect__single-value {
  height: 46px !important;
  margin: 0 !important;
  padding: 0 4px !important;
  color: #394e63 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 46px !important;
  left: 48px !important;
}

::v-deep .patient-search-select .vue-treeselect__placeholder {
  color: #8191a2 !important;
  font-weight: 400 !important;
}

::v-deep .patient-search-select .vue-treeselect__input-container {
  width: 100% !important;
  max-width: 100% !important;
  height: 46px !important;
  margin: 0 !important;
  padding: 0 4px !important;
  border: 0 !important;
  background: transparent !important;
}

::v-deep .patient-search-select input.vue-treeselect__input {
  width: 100% !important;
  max-width: 100% !important;
  height: 46px !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #213950 !important;
  font-family: inherit !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 46px !important;
  vertical-align: top !important;
}

::v-deep .patient-search-select.vue-treeselect--multi .vue-treeselect__input-container {
  width: auto !important;
  min-width: 130px;
  height: 32px !important;
  padding: 0 3px !important;
  vertical-align: top;
}

::v-deep .patient-search-select.vue-treeselect--multi input.vue-treeselect__input {
  height: 32px !important;
  line-height: 32px !important;
}

::v-deep .patient-search-select .vue-treeselect__x-container,
::v-deep .patient-search-select .vue-treeselect__control-arrow-container {
  width: 34px !important;
  height: 46px !important;
  padding: 0 9px !important;
  color: #93a1b0;
  vertical-align: middle;
}

::v-deep .patient-search-select .vue-treeselect__x,
::v-deep .patient-search-select .vue-treeselect__control-arrow {
  width: 8px;
  height: 8px;
}

::v-deep .patient-search-select .vue-treeselect__multi-value {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 40px;
  margin: 0 !important;
}

::v-deep .patient-search-select .vue-treeselect__multi-value-item-container {
  padding: 0 !important;
}

::v-deep .patient-search-select .vue-treeselect__multi-value-item {
  min-height: 30px;
  margin: 0;
  padding: 0;
  border: 1px solid #cde7e3;
  border-radius: 7px;
  background: #eaf7f5;
  color: #117b74;
  font-size: 12px;
  line-height: 1.2;
}

::v-deep .patient-search-select .vue-treeselect__multi-value-label {
  max-width: 230px;
  overflow: hidden;
  padding: 6px 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .patient-search-select .vue-treeselect__value-remove {
  width: 28px;
  padding: 0 8px;
  border-left-color: #cde7e3;
  color: #117b74;
}

::v-deep .patient-search-select .vue-treeselect__menu {
  overflow-x: hidden;
  border-color: #d5e1ea;
  border-radius: 0 0 11px 11px;
  box-shadow: 0 14px 28px rgba(20, 50, 78, 0.13);
}

::v-deep .patient-search-select .vue-treeselect__option {
  min-height: 38px;
  padding-top: 9px;
  padding-bottom: 9px;
  color: #34495e;
  font-size: 12px;
}

::v-deep .patient-search-select .vue-treeselect__option--highlight {
  background: #edf8f6;
  color: #126f69;
}

/* Modern Buttons (Mockup Match) */
.btn-modern-submit {
  background: linear-gradient(135deg, #1E4F7D 0%, #1E958F 100%) !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  padding: 12px 32px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(30, 149, 143, 0.3) !important;
  transition: all 0.2s ease-in-out !important;
  font-size: 1.05rem !important;
}

.btn-modern-submit:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(30, 149, 143, 0.4) !important;
}

.btn-modern-submit i {
  font-size: 1.2rem;
}

.btn-modern-cancel {
  background: #ffffff !important;
  border: 1px solid #DCDFE6 !important;
  color: #1E4F7D !important;
  font-weight: 700 !important;
  padding: 12px 32px !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out !important;
  font-size: 1.05rem !important;
}

.btn-modern-cancel:hover {
  background: #f8f9fa !important;
  border-color: #c0c4cc !important;
  color: #1a446b !important;
}
</style>
<style>
/* style button */
.btn-circle {
  width: 30px;
  height: 30px;
  border-radius: 35px;
  padding: 0;
}

.btn-circle-sm {
  width: 18px;
  height: 18px;
  border-radius: 30px;
  padding: 0;
}

.btn-circle i {
  font-size: 17px;
}

.image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
}
</style>
